import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import {
  Download, FileSignature, Clock, Users, TrendingUp, Coins, Gift,
  ChevronRight, ChevronDown, Wrench, Umbrella,
} from "lucide-react";

const PTO_LABELS = {
  vacation: "Vacation",
  sick: "Sick",
  personal: "Personal",
  holiday: "Holiday",
  bereavement: "Bereavement",
  jury: "Jury Duty",
};

// Returns an array of {benefit_type, hours, rate, pay, label} for the paystub.
const ptoLinesOf = (p) => {
  const rows = p?.deductions?.pto_details;
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => Number(r?.hours || 0) > 0)
    .map((r) => ({
      ...r,
      label: PTO_LABELS[r?.benefit_type] || String(r?.benefit_type || "PTO"),
    }));
};

/* ------------------------------ small helpers ------------------------------ */

const grossOf = (p) => {
  const base = Number(p?.calc?.gross_pay || 0);
  const tips = Number(p?.tips || 0);
  const bonus = Number(p?.bonus || 0);
  const ptoPay = Number(p?.deductions?.pto_pay || 0);
  // Some pay-runs bake tips+bonus/PTO into calc.gross_pay via the paystub-deductions edit
  // endpoint; others still store them as separate columns and use the top-level
  // `gross_pay` field. Prefer the top-level `gross_pay` when it's larger — that's
  // the "effective" gross the net_pay was computed against.
  const stored = Number(p?.gross_pay || 0);
  return Math.max(stored, base + tips + bonus + ptoPay, base);
};

const deductionsOf = (p) => {
  const d = p?.deductions || {};
  return {
    federal: Number(d.federal_tax || 0),
    state: Number(d.state_tax || 0),
    local: Number(d.local_tax || 0),
    fica: Number(d.other_deduction || 0), // SS + Medicare aggregated in .other_deduction
    reimbursement: Number(d.reimbursement || 0),
    adjustment: Number(d.adjustment || 0),
  };
};

const totalWithheld = (p) => {
  const d = deductionsOf(p);
  return d.federal + d.state + d.local + d.fica - d.reimbursement - d.adjustment;
};

/* ------------------------------ small primitives ------------------------------ */

const StatChip = ({ Icon, label, value, sub, tone = "slate" }) => {
  const tones = {
    slate:   "bg-slate-50   border-slate-200   text-slate-900",
    sky:     "bg-sky-50     border-sky-200     text-sky-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50   border-amber-200   text-amber-900",
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} p-3`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-70">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p className="text-lg font-extrabold font-display tracking-tight mt-0.5">{value}</p>
      {sub && <p className="text-[11px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

const KV = ({ label, value, sub, bold = false, indent = false }) => (
  <div
    className={`flex items-baseline justify-between ${indent ? "pl-4" : ""} ${
      bold ? "font-bold text-slate-900" : "text-slate-700"
    }`}
  >
    <span className="text-sm">
      {label}
      {sub && <span className="text-[11px] text-slate-500 ml-1.5">{sub}</span>}
    </span>
    <span className={`tabular-nums text-sm ${bold ? "font-extrabold" : "font-semibold"}`}>
      {value}
    </span>
  </div>
);

/* ------------------------------ day breakdown ------------------------------ */

const DayBreakdown = ({ paystub }) => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/daily/submissions", {
          params: {
            date_from: paystub.week_start,
            date_to: paystub.week_end,
            include_unfinalized: true,
            user_id: paystub.user_id,
          },
        });
        if (alive) setRows(data || []);
      } catch (e) {
        if (alive) setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { alive = false; };
    // Refetch when a different paystub is opened. `api` / `formatApiError`
    // are stable imports; `paystub` sub-fields are the actual triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paystub.id, paystub.week_start, paystub.week_end, paystub.user_id]);

  if (err) return <div className="text-xs text-red-700 px-3 py-2">Couldn&apos;t load daily breakdown: {err}</div>;
  if (rows === null) return <div className="text-xs text-slate-500 px-3 py-2">Loading day-by-day…</div>;

  // Build 7 rows for the week, even for days with no submission
  const days = [];
  const start = new Date(paystub.week_start + "T00:00:00");
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const sub = rows.find((r) => r.date === iso);
    const jobs = Number(sub?.production?.total_jobs || sub?.production?.jobs_count || (sub?.production?.jobs?.length ?? 0)) || 0;
    days.push({
      iso,
      label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
      hours: Number(sub?.hours?.net_hours || 0),
      hourly$: Number(sub?.pay?.estimated_hourly_pay || 0),
      jobs,
      commission$: Number(sub?.pay?.estimated_commission_pay || 0),
    });
  }

  const totalHourly = days.reduce((a, d) => a + d.hourly$, 0);
  const totalComm = days.reduce((a, d) => a + d.commission$, 0);
  const totalHours = days.reduce((a, d) => a + d.hours, 0);
  const totalJobs = days.reduce((a, d) => a + d.jobs, 0);
  const weeklyWinner = totalComm > totalHourly ? "commission" : "hourly";
  const winningTotal = weeklyWinner === "commission" ? totalComm : totalHourly;

  // PTO summary — a single line above the week table. PTO isn't a per-day
  // punch, so admins log it as hours-per-benefit-type on the paystub. We show
  // it here as a summary so the employee sees why their gross is higher than
  // the sum of hourly/commission alone.
  const ptoLines = ptoLinesOf(paystub);
  const ptoTotalHours = ptoLines.reduce((a, r) => a + Number(r.hours || 0), 0);
  const ptoTotalPay = ptoLines.reduce((a, r) => a + Number(r.pay || 0), 0);

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden text-xs" data-testid="paystub-day-breakdown">
      {ptoLines.length > 0 && (
        <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 flex items-baseline justify-between gap-2" data-testid="paystub-pto-summary">
          <div className="flex items-center gap-1.5">
            <Umbrella className="h-3.5 w-3.5 text-sky-700" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-sky-900">PTO this period</span>
            <span className="text-[11px] text-sky-800">
              {ptoLines.map((r) => `${r.label} ${Number(r.hours).toFixed(2)}h`).join(" · ")}
            </span>
          </div>
          <span className="tabular-nums font-bold text-sky-900 text-[12px]">
            {ptoTotalHours.toFixed(2)}h · {fmtMoney(ptoTotalPay)}
          </span>
        </div>
      )}
      <div className="grid grid-cols-[1.05fr_46px_70px_40px_75px] gap-2 bg-slate-50 px-3 py-1.5 font-bold uppercase tracking-widest text-[10px] text-slate-500">
        <span>Day</span>
        <span className="text-right">Hrs</span>
        <span className="text-right">Hourly&nbsp;$</span>
        <span className="text-right">Jobs</span>
        <span className="text-right">Comm&nbsp;$</span>
      </div>
      {days.map((d) => {
        const commWins = d.commission$ > d.hourly$ && d.commission$ > 0;
        const hourlyWins = d.hourly$ > d.commission$ && d.hourly$ > 0;
        const empty = d.hours === 0 && d.jobs === 0;
        return (
          <div key={d.iso} className="grid grid-cols-[1.05fr_46px_70px_40px_75px] gap-2 px-3 py-1.5 border-t border-slate-100 tabular-nums">
            <span className={empty ? "text-slate-400" : "text-slate-800"}>{d.label}</span>
            <span className="text-right">{d.hours.toFixed(2)}</span>
            <span className={`text-right ${hourlyWins ? "text-emerald-700 font-bold" : empty ? "text-slate-400" : "text-slate-700"}`}>
              {fmtMoney(d.hourly$)}
            </span>
            <span className="text-right">{d.jobs}</span>
            <span className={`text-right ${commWins ? "text-emerald-700 font-bold" : empty ? "text-slate-400" : "text-slate-700"}`}>
              {fmtMoney(d.commission$)}
            </span>
          </div>
        );
      })}
      <div className="grid grid-cols-[1.05fr_46px_70px_40px_75px] gap-2 px-3 py-1.5 bg-slate-100 font-bold tabular-nums text-[11px]">
        <span>Total</span>
        <span className="text-right">{totalHours.toFixed(2)}</span>
        <span className="text-right">{fmtMoney(totalHourly)}</span>
        <span className="text-right">{totalJobs}</span>
        <span className="text-right">{fmtMoney(totalComm)}</span>
      </div>
      <div className="px-3 py-1.5 bg-emerald-50 border-t border-emerald-200 text-[10px] text-emerald-800">
        <span className="font-bold">Green</span> = which pay method won that day. Weekly total uses the higher of the two:{" "}
        <b>{fmtMoney(winningTotal)}</b> ({weeklyWinner}).
      </div>
    </div>
  );
};

/* ------------------------------ list card ------------------------------ */

const PaystubCard = ({ p, onSign, onDownload }) => {
  const gross = grossOf(p);
  const withheld = totalWithheld(p);
  const tips = Number(p?.tips || 0);
  const bonus = Number(p?.bonus || 0);
  const commPct = Number(p?.calc?.commission_percentage || 0);
  const method = p?.calc?.pay_method_used || "higher_of";
  const status = p?.status;
  return (
    <Card className="rounded-2xl border-slate-200" data-testid={`paystub-card-${p.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Pay period</p>
            <p className="font-bold text-slate-900 leading-tight">
              {fmtDate(p.week_start)} – {fmtDate(p.week_end)}
            </p>
          </div>
          {status === "awaiting_signature" && (
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Awaiting signature</Badge>
          )}
          {status === "acknowledged" && (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Signed</Badge>
          )}
          {status === "draft" && (
            <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Draft</Badge>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Hours</p>
            <p className="text-lg font-extrabold font-display text-slate-900 tabular-nums">
              {Number(p?.calc?.total_hours || 0).toFixed(2)}h
            </p>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Method</p>
            <p className="text-lg font-extrabold font-display text-slate-900 capitalize">
              {method === "higher_of" ? "Higher of" : method}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-xs mb-3">
          {Number(p?.calc?.commission_pay || 0) > 0 && (
            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-slate-600">Commission ({commPct}%)</span>
              <span className="tabular-nums font-semibold text-slate-900">{fmtMoney(p.calc.commission_pay)}</span>
            </div>
          )}
          {Number(p?.calc?.hourly_total || 0) > 0 && (
            <>
              <div className="flex items-center justify-between px-3 py-1.5">
                <span className="text-slate-600">
                  Regular ({Number(p?.calc?.regular_hours || 0).toFixed(2)}h × {fmtMoney(p?.calc?.hourly_rate || 0)})
                </span>
                <span className="tabular-nums font-semibold text-slate-900">{fmtMoney(p?.calc?.regular_pay || 0)}</span>
              </div>
              {Number(p?.calc?.overtime_hours || 0) > 0 && (
                <div className="flex items-center justify-between px-3 py-1.5">
                  <span className="text-slate-600">
                    Overtime ({Number(p?.calc?.overtime_hours || 0).toFixed(2)}h × {fmtMoney(p?.calc?.overtime_rate || 0)})
                  </span>
                  <span className="tabular-nums font-semibold text-slate-900">{fmtMoney(p?.calc?.overtime_pay || 0)}</span>
                </div>
              )}
              <div className="flex items-center justify-between px-3 py-1 bg-slate-50/70">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Hourly total</span>
                <span className="tabular-nums text-[11px] font-bold text-slate-700">{fmtMoney(p.calc.hourly_total)}</span>
              </div>
            </>
          )}
          {Number(p?.calc?.flsa_ot_premium || 0) > 0 && (
            <div className="flex items-center justify-between px-3 py-1.5" title="FLSA 29 CFR 778.118 — half-time OT premium on commission earnings">
              <span className="text-slate-600">
                FLSA OT premium ({Number(p?.calc?.overtime_hours || 0).toFixed(2)}h × ½ × {fmtMoney(p?.calc?.weekly_regular_rate || 0)}/hr)
              </span>
              <span className="tabular-nums font-semibold text-slate-900">{fmtMoney(p.calc.flsa_ot_premium)}</span>
            </div>
          )}
          {Number(p?.calc?.shop_pay || 0) > 0 && (
            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-slate-600 flex items-center gap-1"><Wrench className="h-3 w-3" /> Shop</span>
              <span className="tabular-nums font-semibold text-slate-900">{fmtMoney(p.calc.shop_pay)}</span>
            </div>
          )}
          {tips > 0 && (
            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-slate-600 flex items-center gap-1"><Coins className="h-3 w-3" /> Tips</span>
              <span className="tabular-nums font-semibold text-slate-900">{fmtMoney(tips)}</span>
            </div>
          )}
          {bonus > 0 && (
            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-slate-600 flex items-center gap-1"><Gift className="h-3 w-3" /> Bonuses</span>
              <span className="tabular-nums font-semibold text-slate-900">{fmtMoney(bonus)}</span>
            </div>
          )}
          <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50">
            <span className="font-bold text-slate-900">Gross</span>
            <span className="tabular-nums font-extrabold text-slate-900">{fmtMoney(gross)}</span>
          </div>
          {withheld > 0 && (
            <div className="flex items-center justify-between px-3 py-1.5 text-red-700">
              <span>Withheld (fed + state + FICA)</span>
              <span className="tabular-nums font-semibold">− {fmtMoney(withheld)}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 mb-3">
          <span className="text-sm font-bold text-emerald-900">Net Pay</span>
          <span className="text-xl font-extrabold font-display text-emerald-800 tabular-nums">{fmtMoney(p.net_pay)}</span>
        </div>

        <div className="flex gap-2">
          {status === "awaiting_signature" && (
            <Button
              onClick={() => onSign(p)}
              className="flex-1 bg-slate-900 hover:bg-slate-800"
              data-testid={`acknowledge-${p.id}-btn`}
            >
              <FileSignature className="h-4 w-4 mr-1" /> Sign &amp; view detail
            </Button>
          )}
          {status !== "awaiting_signature" && (
            <Button
              variant="outline"
              onClick={() => onSign(p)}
              className="flex-1"
              data-testid={`view-detail-${p.id}-btn`}
            >
              View detail
            </Button>
          )}
          <Button variant="outline" onClick={() => onDownload(p.id)} data-testid={`download-pdf-${p.id}-btn`}>
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------------------------ sign-off dialog ------------------------------ */

const SignOffDialog = ({ paystub, open, onOpenChange, onSubmit }) => {
  const [ack, setAck] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [openDays, setOpenDays] = useState(false);

  useEffect(() => {
    if (open) {
      setAck(false);
      setSignature("");
      setOpenDays(false);
    }
  }, [open, paystub?.id]);

  const readonly = paystub?.status !== "awaiting_signature";

  const grossPay = useMemo(() => grossOf(paystub), [paystub]);
  const withheld = useMemo(() => totalWithheld(paystub), [paystub]);
  const d = useMemo(() => deductionsOf(paystub), [paystub]);
  const tips = Number(paystub?.tips || 0);
  const bonus = Number(paystub?.bonus || 0);

  if (!paystub) return null;
  const method = paystub?.calc?.pay_method_used || "higher_of";
  const commPct = Number(paystub?.calc?.commission_percentage || 0);
  const commPay = Number(paystub?.calc?.commission_pay || 0);
  const hourlyTotal = Number(paystub?.calc?.hourly_total || 0);
  const totalHours = Number(paystub?.calc?.total_hours || 0);
  const hourlyRate = Number(paystub?.calc?.hourly_rate || 0);
  const netRev = Number(paystub?.calc?.net_commissionable_revenue || 0);
  const shopPay = Number(paystub?.calc?.shop_pay || 0);
  const shopHours = Number(paystub?.calc?.shop_hours || 0);

  const winnerLabel = method === "commission" || (method === "higher_of" && commPay >= hourlyTotal) ? "commission" : "hourly";
  const extra = Math.abs(commPay - hourlyTotal);

  const submit = async () => {
    if (readonly || !ack || !signature) return;
    setBusy(true);
    try {
      await onSubmit(paystub, signature);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto p-0" data-testid="paystub-signoff-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>Paystub Detail</DialogTitle>
        </DialogHeader>

        {/* Header — net pay + pay period */}
        <div className="bg-slate-900 text-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Net Pay</p>
              <p className="text-4xl font-extrabold font-display text-emerald-400 leading-none mt-1" data-testid="paystub-net-pay">
                {fmtMoney(paystub.net_pay)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Pay Period</p>
              <p className="text-xs font-semibold text-slate-200 leading-tight mt-1">
                {fmtDate(paystub.week_start)}
                <br />→ {fmtDate(paystub.week_end)}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Quick chips */}
          <div className="grid grid-cols-3 gap-2">
            <StatChip Icon={Clock} label="Hours" value={`${totalHours.toFixed(2)}h`} sub={hourlyRate > 0 ? `${fmtMoney(hourlyRate)}/h` : undefined} tone="slate" />
            <StatChip Icon={Users} label="Net Rev" value={fmtMoney(netRev)} sub={commPct > 0 ? `${commPct}% comm` : undefined} tone="sky" />
            <StatChip Icon={TrendingUp} label="Method" value={winnerLabel === "commission" ? "Comm." : "Hourly"} sub="Higher paid" tone="emerald" />
          </div>

          {/* Earnings */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Earnings</p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1.5">
              {commPay > 0 && (
                <KV
                  label={`Commission (${commPct}% of ${fmtMoney(netRev)})`}
                  value={fmtMoney(commPay)}
                  sub={winnerLabel === "commission" ? "✓ used" : "not used"}
                />
              )}
              {(hourlyTotal > 0 || totalHours > 0) && (
                <KV
                  label={`Hourly (${totalHours.toFixed(2)}h × ${fmtMoney(hourlyRate)})`}
                  value={fmtMoney(hourlyTotal)}
                  sub={winnerLabel === "hourly" ? "✓ used" : "not used"}
                />
              )}
              {commPay > 0 && hourlyTotal > 0 && (
                <div className="text-[10px] text-emerald-800 bg-emerald-50 rounded-md px-2 py-1 border border-emerald-200 mt-1">
                  You&apos;re paid whichever is higher — this pay period that&apos;s <b>{winnerLabel}</b>, an extra {fmtMoney(extra)} over the other option.
                </div>
              )}
              {shopPay > 0 && (
                <>
                  <div className="border-t border-slate-200 my-2" />
                  <KV label={`Shop time (${shopHours.toFixed(2)}h)`} value={fmtMoney(shopPay)} />
                </>
              )}
              {(tips > 0 || bonus > 0) && <div className="border-t border-slate-200 my-2" />}
              {tips > 0 && <KV label="Tips" value={fmtMoney(tips)} sub="pre-tax" />}
              {bonus > 0 && <KV label="Bonus" value={fmtMoney(bonus)} sub="pre-tax" />}
              {ptoLinesOf(paystub).length > 0 && (
                <>
                  <div className="border-t border-slate-200 my-2" />
                  {ptoLinesOf(paystub).map((row) => (
                    <KV
                      key={row.benefit_type}
                      label={`PTO — ${row.label} (${Number(row.hours).toFixed(2)}h)`}
                      value={fmtMoney(row.pay)}
                      sub="pre-tax"
                    />
                  ))}
                </>
              )}
              <div className="border-t border-slate-200 my-2" />
              <KV label="Gross Pay" value={fmtMoney(grossPay)} bold />
            </div>
          </div>

          {/* Withholdings */}
          {(paystub?.taxes_enabled || withheld > 0) && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Withholdings</p>
              <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1.5">
                {d.federal > 0 && <KV label="Federal" value={`− ${fmtMoney(d.federal)}`} indent />}
                {d.state > 0 && <KV label="State" value={`− ${fmtMoney(d.state)}`} indent />}
                {d.local > 0 && <KV label="Local" value={`− ${fmtMoney(d.local)}`} indent />}
                {d.fica > 0 && <KV label="FICA (SS + Medicare)" value={`− ${fmtMoney(d.fica)}`} indent />}
                {d.reimbursement > 0 && <KV label="Reimbursement" value={`+ ${fmtMoney(d.reimbursement)}`} indent />}
                {d.adjustment !== 0 && <KV label="Adjustment" value={`${d.adjustment > 0 ? "+" : "− "}${fmtMoney(Math.abs(d.adjustment))}`} indent />}
                <div className="border-t border-slate-200 my-2" />
                <KV label="Total Withheld" value={`− ${fmtMoney(withheld)}`} bold />
              </div>
            </div>
          )}

          {/* Day-by-day expand */}
          <button
            type="button"
            onClick={() => setOpenDays((v) => !v)}
            className="w-full flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="paystub-day-breakdown-toggle"
          >
            <span>Day-by-day breakdown</span>
            {openDays ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          {openDays && <DayBreakdown paystub={paystub} />}

          {/* Acknowledgment */}
          {paystub?.status === "acknowledged" ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900" data-testid="paystub-already-signed">
              You signed this paystub{paystub.acknowledged_at ? ` on ${fmtDate(paystub.acknowledged_at.slice(0, 10))}` : ""}.
            </div>
          ) : paystub?.status === "draft" ? (
            <div className="rounded-xl bg-slate-100 border border-slate-200 px-3 py-2 text-xs text-slate-700" data-testid="paystub-draft">
              This paystub is still a draft — your admin hasn&apos;t finalized it yet, so it&apos;s not ready to sign.
            </div>
          ) : paystub?.status === "awaiting_signature" ? (
            <>
              <label className="flex items-start gap-3 cursor-pointer pt-1">
                <Checkbox checked={ack} onCheckedChange={setAck} data-testid="ack-checkbox" />
                <span className="text-xs text-slate-700">
                  I acknowledge that the hours, jobs, tips, bonuses and withholdings above are correct, and I&apos;ve received{" "}
                  <b>{fmtMoney(paystub.net_pay)}</b> for the pay period {fmtDate(paystub.week_start)} → {fmtDate(paystub.week_end)}.
                </span>
              </label>
              <SignaturePad value={signature} onChange={setSignature} testId="paystub-signature-pad" />
              <Button
                onClick={submit}
                disabled={!ack || !signature || busy}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-bold"
                data-testid="submit-ack-btn"
              >
                {busy ? "Signing…" : "Sign & Submit"}
              </Button>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------ page ------------------------------ */

export default function Paystubs() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get("/paystubs");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const onSign = (p) => { setSelected(p); setOpen(true); };
  const onDownload = async (id) => {
    const resp = await api.get(`/reports/paystub-pdf/${id}`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = `paystub-${id}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const submitAck = async (paystub, signature) => {
    try {
      await api.post(`/paystubs/${paystub.id}/acknowledge`, { acknowledged: true, signature_data: signature });
      toast.success("Paystub acknowledged");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="paystubs-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Paystubs</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">My Paystubs</h1>
      </header>

      {items.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-2 border-slate-300">
          <CardContent className="py-10 text-center text-slate-500">No paystubs yet.</CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <PaystubCard key={p.id} p={p} onSign={onSign} onDownload={onDownload} />
          ))}
        </div>
      )}

      <SignOffDialog paystub={selected} open={open} onOpenChange={setOpen} onSubmit={submitAck} />
    </div>
  );
}
